import React, { useEffect, useRef } from 'react'
import { Modal, Button, Row, Col } from 'react-bootstrap'

interface ConfirmDialogProps {
  show: boolean
  onHide: () => void
  onConfirmLeft?: () => void
  onConfirmRight: () => void
  title: string
  message: React.ReactNode
  confirmLeftText?: string
  confirmRightText?: string
  cancelText?: string
  variant?: 'primary' | 'danger' | 'warning' | 'success'
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  show,
  onHide,
  onConfirmLeft,
  onConfirmRight,
  title,
  message,
  confirmRightText = 'Continue',
  cancelText,
  variant = 'primary',
  confirmLeftText,
}) => {
  const modalRef = useRef<HTMLDivElement | null>(null)

  const handleConfirm = () => {
    onConfirmRight()
    onHide()
  }

  useEffect(() => {
    if (!show) return
    const node = modalRef.current
    const handleKeyDown = (e: KeyboardEvent) => {
      const key = e.key
      if (key === 'Escape') {
        e.preventDefault()
        e.stopPropagation()
        onHide()
        return
      }
      if (key === 'Enter') {
        e.preventDefault()
        e.stopPropagation()
        if (confirmRightText) handleConfirm()
      }
    }
    document.addEventListener('keydown', handleKeyDown, true)
    return () => document.removeEventListener('keydown', handleKeyDown, true)
  }, [show, onHide, confirmRightText])

  return (
    <Modal
      className="create-project-modal"
      show={show}
      onHide={onHide}
      centered
      backdrop="static"
      keyboard={false}
    >
      <div
        ref={modalRef}
        onKeyDownCapture={(e) => {
          if (e.key === 'Tab') {
            e.stopPropagation()
          }
        }}
      >
        <Modal.Header className={`bg-${variant} text-white`}>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{message}</Modal.Body>
        <Modal.Footer>
          <Row className="w-100">
            <Col xs={6} className="d-flex justify-content-start">
              {cancelText && (
                <Button variant="secondary" onClick={onHide}>
                  {cancelText}
                </Button>
              )}
            </Col>
            <Col xs={6} className="d-flex justify-content-end gap-3">
              {onConfirmLeft && confirmLeftText && (
                <Button variant="outline-primary" onClick={onConfirmLeft}>
                  {confirmLeftText}
                </Button>
              )}
              {confirmRightText && (
                <Button variant={variant} onClick={handleConfirm} autoFocus>
                  {confirmRightText}
                </Button>
              )}
            </Col>
          </Row>
        </Modal.Footer>
      </div>
    </Modal>
  )
}

export default ConfirmDialog
